import { type Component, For, type JSX, Show } from 'solid-js'
import '../Tabs/navSelected.css'

export interface SegmentedControlOption<T extends string> {
  value: T
  label: string
  icon?: Component<{ class?: string }>
  disabled?: boolean
}

export type SegmentedControlIconOption<T extends string> = SegmentedControlOption<T> & {
  icon: Component<{ class?: string }>
}

export type SegmentedControlSize = 'sm' | 'md' | 'toolbar' | 'toolbarMd'

export type SegmentedControlVariant = 'solid' | 'raised'

interface SegmentedControlBaseProps<T extends string> {
  value: T
  onChange: (value: T) => void
  label: string
  minItemWidth?: string
  size?: SegmentedControlSize
  stretch?: boolean
  /** Toolbar sizes only: each name sits under its icon in small type and the parts share the full width, so a narrow screen keeps every name. */
  stacked?: boolean
  variant?: SegmentedControlVariant | undefined
  class?: string
}

export type SegmentedControlProps<T extends string> = SegmentedControlBaseProps<T> &
  (
    | { options: SegmentedControlOption<T>[]; iconOnly?: false }
    | { options: SegmentedControlIconOption<T>[]; iconOnly: true }
  )

const smSegmentShape = 'min-h-8 gap-1.5 rounded-[var(--radius-8)] border px-3 py-1'
const smTrackSurface =
  'gap-1 rounded-[var(--radius-12)] border border-[var(--color-border)] bg-[var(--color-neutral-100)] p-1'
const stretchedSmTrack = `grid w-full auto-cols-fr grid-flow-col ${smTrackSurface}`
const stretchedSmSegment = `${smSegmentShape} min-w-0 text-center`
const stackedTrack =
  'grid w-full auto-cols-fr grid-flow-col gap-1 rounded-[var(--radius-12)] border border-[var(--color-border)] bg-[var(--color-surface)] p-1'
const stackedSegment = 'min-w-0 flex-col gap-0.5 rounded-[var(--radius-8)] px-1 py-1.5'
const stackedLabel = 'max-w-full truncate text-[length:var(--text-xs)] font-semibold leading-tight'

const primaryFocusOutline =
  'focus-visible:-outline-offset-2 focus-visible:outline-[var(--color-primary-500)]'

const raisedTrackSurface =
  'gap-1 rounded-[var(--radius-12)] border border-[var(--color-border)] bg-[var(--color-neutral-100)] p-1 shadow-[var(--shadow-control)]'
const raisedTrack = `inline-flex h-10 max-w-full shrink-0 ${raisedTrackSurface}`
const raisedStackedTrack = `grid w-full auto-cols-fr grid-flow-col ${raisedTrackSurface}`
const raisedOption = {
  on: `ds-nav-selected text-[color:var(--color-text)] ${primaryFocusOutline}`,
  off: `border border-transparent bg-transparent text-[color:var(--color-neutral-800)] hover:bg-[var(--color-surface)]/55 hover:text-[color:var(--color-text)] ${primaryFocusOutline}`,
}
const optionIcon = 'w-4 h-4 shrink-0'
const raisedSelectedIcon = 'text-[color:var(--color-primary-500)]'

const optionClasses: Record<
  SegmentedControlSize,
  { base: string; iconOnlyBase?: string; on: string; off: string }
> = {
  md: {
    base: 'min-h-11 gap-1.5 rounded-[var(--radius-12)] border px-3 py-3 sm:gap-2 sm:px-4',
    on: 'bg-[var(--color-neutral-900)] text-[var(--color-neutral-50)] border-[var(--color-neutral-900)] focus-visible:-outline-offset-4 focus-visible:outline-[var(--color-neutral-50)]',
    off: `bg-[var(--color-surface)] text-[var(--color-text)] border-[var(--color-border)] hover:border-[var(--color-neutral-300)] ${primaryFocusOutline}`,
  },
  sm: {
    base: `${smSegmentShape} whitespace-nowrap`,
    on: `bg-[var(--segmented-selected)] text-[color:var(--color-text)] border-[var(--color-field-border)] shadow-[var(--shadow-control)] ${primaryFocusOutline}`,
    off: `border-transparent bg-transparent text-[color:var(--color-text-muted)] hover:bg-[var(--segmented-selected)]/60 hover:text-[color:var(--color-text)] ${primaryFocusOutline}`,
  },
  toolbar: {
    base: 'h-full gap-1.5 whitespace-nowrap rounded-[var(--radius-8)] px-3',
    iconOnlyBase: 'h-full aspect-square rounded-[var(--radius-8)]',
    on: 'bg-[var(--color-text)] text-[color:var(--color-surface)] focus-visible:-outline-offset-4 focus-visible:outline-[var(--color-surface)]',
    off: `bg-transparent text-[color:var(--color-text-muted)] hover:text-[color:var(--color-text)] ${primaryFocusOutline}`,
  },
  toolbarMd: {
    base: 'h-full gap-1.5 whitespace-nowrap rounded-[var(--radius-8)] px-3',
    iconOnlyBase: 'h-full aspect-square rounded-[var(--radius-8)]',
    on: 'bg-[var(--color-text)] text-[color:var(--color-surface)] focus-visible:-outline-offset-4 focus-visible:outline-[var(--color-surface)]',
    off: `bg-transparent text-[color:var(--color-text-muted)] hover:text-[color:var(--color-text)] ${primaryFocusOutline}`,
  },
}

const trackClasses: Record<SegmentedControlSize, string> = {
  md: 'grid grid-cols-[repeat(auto-fit,minmax(var(--segmented-min-item),1fr))] gap-2',
  sm: `inline-flex max-w-full flex-wrap ${smTrackSurface}`,
  toolbar:
    'inline-flex h-10 max-w-full shrink-0 gap-1 rounded-[var(--radius-12)] border border-[var(--color-border)] bg-[var(--color-surface)] p-1',
  toolbarMd:
    'inline-flex h-10 max-w-full shrink-0 gap-1 rounded-[var(--radius-12)] border border-[var(--color-border)] bg-[var(--color-surface)] p-1',
}

const labelClasses: Record<SegmentedControlSize, string> = {
  md: 'text-sm font-medium leading-normal',
  sm: 'text-sm font-semibold leading-normal',
  toolbar: 'text-[length:var(--text-ui)] font-semibold leading-normal',
  toolbarMd: 'text-[length:var(--text-ui)] font-semibold leading-normal',
}

export function SegmentedControl<T extends string>(props: SegmentedControlProps<T>): JSX.Element {
  const options = (): SegmentedControlOption<T>[] => props.options
  const selectable = () => options().filter((o) => !o.disabled)
  const buttons = new Map<T, HTMLButtonElement>()

  const selectAndFocus = (value: T) => {
    props.onChange(value)
    buttons.get(value)?.focus()
  }

  const move = (from: T, delta: number) => {
    const pool = selectable()
    if (pool.length === 0) {
      return
    }
    const current = pool.findIndex((o) => o.value === from)
    const next = pool[(current + delta + pool.length) % pool.length]
    if (next) {
      selectAndFocus(next.value)
    }
  }

  const onKeyDown = (e: KeyboardEvent, value: T) => {
    switch (e.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        e.preventDefault()
        move(value, 1)
        break
      case 'ArrowLeft':
      case 'ArrowUp':
        e.preventDefault()
        move(value, -1)
        break
      case 'Home': {
        e.preventDefault()
        const first = selectable()[0]
        if (first) {
          selectAndFocus(first.value)
        }
        break
      }
      case 'End': {
        e.preventDefault()
        const pool = selectable()
        const last = pool[pool.length - 1]
        if (last) {
          selectAndFocus(last.value)
        }
        break
      }
    }
  }

  const size = () => props.size ?? 'md'
  const stretched = () => size() === 'sm' && props.stretch === true
  const toolbarSize = () => size() === 'toolbar' || size() === 'toolbarMd'
  const stacked = () => toolbarSize() && props.stacked === true && !props.iconOnly
  const raised = () => toolbarSize() && props.variant === 'raised'
  const tone = () => (raised() ? raisedOption : optionClasses[size()])
  const segmentShape = () => {
    if (stacked()) {
      return stackedSegment
    }
    if (stretched()) {
      return stretchedSmSegment
    }
    const classes = optionClasses[size()]
    return (props.iconOnly && classes.iconOnlyBase) || classes.base
  }
  const trackShape = () => {
    if (raised()) {
      return stacked() ? raisedStackedTrack : raisedTrack
    }
    if (stacked()) {
      return stackedTrack
    }
    return stretched() ? stretchedSmTrack : trackClasses[size()]
  }

  return (
    <div
      role="radiogroup"
      aria-label={props.label}
      class={`${trackShape()} ${props.class ?? ''}`}
      style={size() === 'md' ? { '--segmented-min-item': props.minItemWidth ?? '7rem' } : undefined}
    >
      <For each={options()}>
        {(option) => {
          const isSelected = () => props.value === option.value
          return (
            <button
              ref={(el) => buttons.set(option.value, el)}
              type="button"
              role="radio"
              aria-checked={isSelected()}
              aria-label={props.iconOnly ? option.label : undefined}
              title={props.iconOnly || stacked() ? option.label : undefined}
              disabled={option.disabled}
              tabIndex={isSelected() ? 0 : -1}
              onClick={() => props.onChange(option.value)}
              onKeyDown={(e) => onKeyDown(e, option.value)}
              class={`ds-segment flex items-center justify-center transition-all focus-visible:outline-2 disabled:cursor-not-allowed disabled:opacity-50 ${segmentShape()} ${
                isSelected() ? tone().on : tone().off
              }`}
            >
              {option.icon?.({
                class:
                  raised() && isSelected() ? `${optionIcon} ${raisedSelectedIcon}` : optionIcon,
              })}
              <Show when={!props.iconOnly}>
                <span class={stacked() ? stackedLabel : labelClasses[size()]}>{option.label}</span>
              </Show>
            </button>
          )
        }}
      </For>
    </div>
  )
}
